import { Fragment, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { formatAmount } from "@/utils/formatAmount";
import { displayUnit } from "@/utils/displayUnit";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Upload,
  Paperclip,
  ExternalLink,
  RotateCcw,
  Edit2,
  Clock,
  Plus,
  Trash2,
  GitBranch,
} from "lucide-react";
import apiClient from "@/api/client";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { DatePicker } from "@/components/ui/DatePicker";
import { Select } from "@/components/ui/Select";
import { useAuthStore } from "@/store/auth";
import { PageLoader, Spinner } from "@/components/ui/Spinner";
import { ManagedFileLink, type ScanStatus } from "@/components/ui/ManagedFileLink";
import { usePageTitle } from "@/hooks/usePageTitle";
import { InvoiceAiBadge, InvoiceAiInlineAlert, type InvoiceAiAnalysis } from "@/features/invoices/components/InvoiceAiBadge";
import { acceptedQtyFromPct, amountFromQty, completionPctFromQty, isAmountOverridden, itemAmountNet, qtyInputValue } from "@/features/deliveries/utils/pockMath";
import { DocumentAttachmentsPanel } from "@/features/deliveries/components/DocumentAttachmentsPanel";
import type { DocumentAttachment, ProtocolCorrection } from "@/features/deliveries/types";
import { apiErrorText } from "@/features/deliveries/utils/apiErrorText";

interface AttachmentVersion {
  id: string;
  managed_file_detail: {
    id: string;
    original_filename: string;
    content_type: string;
    size_bytes: number;
    url: string | null;
  } | null;
  original_name: string;
  is_superseded: boolean;
  uploaded_by_name: string;
  created_at: string;
}

interface ProtocolItem {
  id: string;
  order_line_item: string;
  order_line_item_name: string;
  order_line_item_unit: string;
  order_line_item_quantity: string;
  // null when the viewer may not see rates (rate_visibility — same matrix as the order)
  order_line_item_unit_rate: string | null;
  completion_percentage: string;
  quality_pct: string;
  amount_net: string | null;
  attachment_managed_file: string | null;
  attachment_managed_file_detail: {
    id: string;
    original_filename: string;
    content_type: string;
    size_bytes: number;
    url: string | null;
  } | null;
  attachment_versions: AttachmentVersion[];
}

interface Protocol {
  id: string;
  protocol_number: string;
  order: string;
  order_number: string;
  contractor: string;
  contractor_name: string;
  contractor_is_vat_payer: boolean;
  contractor_entity_type?: "company" | "individual";
  project: string;
  project_name: string;
  is_final: boolean;
  is_withdrawn: boolean;
  /** POCK bez kwot — przekazanie prac przez pracownika z dostępem „Realizacja”; bez faktury/UZ. */
  without_amounts?: boolean;
  accepted_by_contractor: boolean | null;
  /** POCK końcowy bez kwot czeka na akceptację ZAMAWIAJĄCEGO (nie podwykonawcy). */
  awaiting_orderer_acceptance?: boolean;
  /** Zalogowany zarządza zamówieniem i nie wystawił tego POCK — może odpowiedzieć. */
  can_respond_as_orderer?: boolean;
  contractor_rejection_comment: string;
  created_by: string;
  created_by_name: string;
  created_at: string;
  notes: string;
  items: ProtocolItem[];
  pock_approved_settlement_pct?: string;
  requires_hr_mandate?: boolean;
  hr_mandate_status?: string;
  generated_pdf_managed_file_detail?: {
    id: string;
    original_filename: string;
    url: string | null;
  } | null;
  /** Files of the whole protocol (not of one line). */
  document_attachments?: DocumentAttachment[];
  /** Null on list payloads; on the card: may the viewer add files to the whole protocol. */
  can_add_attachments?: boolean | null;
  /** The POCK this one was created from (correction history) — may be deleted since. */
  source_protocol?: string | null;
  source_protocol_number?: string | null;
  source_protocol_deleted?: boolean;
  corrections?: ProtocolCorrection[];
  /** Technical director / CEO on a rejected POCK without settlement. */
  can_delete?: boolean;
  delete_block_reason?: string | null;
}

const CORRECTION_STATUS_LABEL: Record<ProtocolCorrection["status_contractor_response"], string> = {
  oczekuje: "oczekuje",
  zaakceptowany: "zaakceptowany",
  odrzucony: "odrzucony",
  wycofany: "wycofany",
};

interface InvoiceFileVersion {
  id: string;
  managed_file_detail: {
    id: string;
    original_filename: string;
    content_type: string;
    size_bytes: number;
    url: string | null;
  } | null;
  uploaded_by_name: string;
  created_at: string;
}

interface Invoice {
  id: string;
  invoice_number: string;
  // null when the viewer may not see the order's rates (the invoice restates them)
  amount_net: string | null;
  amount_gross: string | null;
  status: string;
  booking_status: string;
  payment_status: string;
  paid_amount: string;
  status_comment: string;
  payment_comment: string;
  submitted_at: string;
  submitted_by_name: string;
  invoice_managed_file: string | null;
  invoice_managed_file_detail: {
    id: string;
    original_filename: string;
    content_type: string;
    size_bytes: number;
    url: string | null;
    scan_status?: ScanStatus;
  } | null;
  file_versions: InvoiceFileVersion[];
  is_advance_payment: boolean;
  mandate_type?: string;
  mandate_type_label?: string;
  ai_analysis?: InvoiceAiAnalysis | null;
}

interface AuditLog {
  id: string;
  user_name: string;
  description: string;
  created_at: string;
}

const statusColor: Record<string, "gray" | "green" | "red" | "blue"> = {
  submitted: "blue",
  accepted: "green",
  rejected: "red",
  withdrawn: "gray",
};
const statusLabel: Record<string, string> = {
  submitted: "Przesłana",
  accepted: "Przyjęta",
  rejected: "Nieprzyjęta",
  withdrawn: "Wycofana",
};
const paymentColor: Record<string, "gray" | "green" | "yellow"> = {
  unpaid: "gray",
  partially_paid: "yellow",
  paid: "green",
};
const paymentLabel: Record<string, string> = {
  unpaid: "Nieopłacona",
  partially_paid: "Opłacona częściowo",
  paid: "Opłacona",
};

const VAT_RATE = 0.23;

/** Settlement percentage a protocol was approved at (100 when not set). */
function protocolSettlementPct(protocol: Protocol): number {
  return parseFloat(protocol.pock_approved_settlement_pct ?? "100") || 100;
}

function autoItemValue(item: ProtocolItem, settlementPct = 100): string {
  return itemAmountNet(
    item.order_line_item_quantity,
    item.order_line_item_unit_rate ?? "",
    item.completion_percentage,
    item.quality_pct || 100,
    settlementPct,
  ).toFixed(2);
}

/** Both the stored amount and the rate to derive it are hidden from this viewer
 *  (rate_visibility masks them server-side); a visible auto-calculated item
 *  always carries its rate. */
function isItemMasked(item: ProtocolItem): boolean {
  return item.amount_net == null && item.order_line_item_unit_rate == null;
}

export function ProtocolDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const canManage = user?.role === "director" || user?.role === "accountant";
  const canWithdraw = user?.role === "director" || user?.role === "coordinator";
  const canEdit = user?.role === "director" || user?.role === "coordinator";
  const isCeo = user?.is_ceo === true;

  const { data: protocol, isLoading } = useQuery({
    queryKey: ["protocols", id],
    queryFn: () =>
      apiClient
        .get<{ data: Protocol }>(`/protocols/${id}/`)
        .then((r) => r.data.data),
    enabled: !!id,
  });
  usePageTitle(protocol && `POCK ${protocol.protocol_number}`);

  const { data: invoices, isLoading: invoicesLoading } = useQuery({
    queryKey: ["invoices", { protocol: id }],
    queryFn: () =>
      apiClient
        .get<{ data: Invoice[] }>("/invoices/", { params: { protocol: id } })
        .then((r) => r.data.data),
    enabled: !!id,
  });

  const { data: auditLogs } = useQuery({
    queryKey: ["protocol-audit-logs", id],
    queryFn: () =>
      apiClient
        .get<{ data: AuditLog[] }>(`/protocols/${id}/audit-logs/`)
        .then((r) => r.data.data),
    enabled: !!id,
  });

  // --- Edit items state ---
  const [editMode, setEditMode] = useState(false);
  type EditItem = {
    id: string;
    qty: string; // accepted qty (derived from completion_pct / quality_pct * ordered_qty)
    quality_pct: string; // quality %
    amount_net: string;
    is_manual: boolean;
  };
  const [editItems, setEditItems] = useState<EditItem[]>([]);
  const [editIsFinal, setEditIsFinal] = useState(false);

  const enterEditMode = () => {
    if (!protocol) return;
    setEditIsFinal(protocol.is_final);
    setEditItems(
      protocol.items.map((item) => {
        const qualityPct = parseFloat(item.quality_pct || "100") || 100;
        // qty = (completionPct / qualityPct) * Q, snapped onto the quantity grid
        // so that re-saving an untouched form cannot move the amount.
        const qty = qtyInputValue(
          acceptedQtyFromPct(item.completion_percentage, item.order_line_item_quantity, qualityPct),
        );
        return {
          id: item.id,
          qty,
          quality_pct: String(qualityPct),
          amount_net: item.amount_net ?? "",
          // Only a value that disagrees with the formula was actually chosen by
          // a person — approval caches the formula's own result on every line,
          // and calling that an override stops the amount following the qty.
          // Same predicate as the read-only "zmieniona" badge below.
          is_manual: isAmountOverridden(
            item.amount_net,
            autoItemValue(item, protocolSettlementPct(protocol)),
          ),
        };
      }),
    );
    setEditMode(true);
  };

  const updateEditItem = (
    itemId: string,
    field: "qty" | "quality_pct" | "amount_net" | "is_manual",
    value: string | boolean,
  ) => {
    setEditItems((prev) =>
      prev.map((it) => (it.id === itemId ? { ...it, [field]: value } : it)),
    );
  };

  // --- Invoice modal state ---
  const [selected, setSelected] = useState<Invoice | null>(null);
  const [modalType, setModalType] = useState<
    | "status"
    | "booking"
    | "payment"
    | "add"
    | "withdraw"
    | "note"
    | "force_accept"
    | "orderer_accept"
    | "orderer_reject"
    | "delete"
    | null
  >(null);
  // Powód usunięcia odrzuconego POCK (wymagany po stronie backendu).
  const [deleteReason, setDeleteReason] = useState("");
  // Powód odrzucenia POCK bez kwot przez zamawiającego (wymagany po stronie backendu).
  const [ordererComment, setOrdererComment] = useState("");
  const [form, setForm] = useState({
    status: "",
    booking_status: "",
    payment_amount: "",
    payment_date: "",
    comment: "",
  });

  // New invoice / UZ-UoD settlement form
  const [newInvoice, setNewInvoice] = useState({
    invoice_number: "",
    amount_net: "",
    is_advance_payment: false,
    mandate_type: "",
  });
  const [newInvoiceFile, setNewInvoiceFile] = useState<File | null>(null);
  const [invoiceErrors, setInvoiceErrors] = useState<{
    invoice_number?: string;
    amount_net?: string;
    file?: string;
    mandate_type?: string;
  }>({});
  const [invoiceServerError, setInvoiceServerError] = useState("");

  // Note modal
  const [noteText, setNoteText] = useState("");

  // Item attachment upload
  const [uploadingItemId, setUploadingItemId] = useState<string | null>(null);
  const itemFileInputRef = useRef<HTMLInputElement>(null);

  const computedGrossNum = protocol?.contractor_is_vat_payer
    ? parseFloat(newInvoice.amount_net || "0") * (1 + VAT_RATE)
    : parseFloat(newInvoice.amount_net || "0");
  const computedGross = computedGrossNum.toFixed(2);

  // --- Mutations ---
  const updateItemsMutation = useMutation({
    mutationFn: (payload: unknown) =>
      apiClient.patch(`/protocols/${id}/update-items/`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["protocols", id] });
      qc.invalidateQueries({ queryKey: ["protocol-audit-logs", id] });
      setEditMode(false);
    },
  });

  const addNoteMutation = useMutation({
    mutationFn: (description: string) =>
      apiClient.post(`/protocols/${id}/audit-logs/`, { description }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["protocol-audit-logs", id] });
      setNoteText("");
      setModalType(null);
    },
  });

  const actionMutation = useMutation({
    mutationFn: ({ action, payload }: { action: string; payload: unknown }) =>
      apiClient.patch(`/invoices/${selected?.id}/${action}/`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices", { protocol: id }] });
      setModalType(null);
    },
  });

  const addPaymentMutation = useMutation({
    mutationFn: (payload: {
      amount_gross: string;
      payment_date: string;
      comment: string;
    }) => apiClient.post(`/invoices/${selected?.id}/add-payment/`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices", { protocol: id }] });
      setModalType(null);
    },
  });

  // GK-side: run/refresh the AI check of an invoice document on demand (synchronous).
  const analyzeMutation = useMutation({
    mutationFn: (invoiceId: string) => apiClient.post(`/invoices/${invoiceId}/analyze/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invoices", { protocol: id }] }),
  });

  const addInvoiceMutation = useMutation({
    mutationFn: (formData: FormData) =>
      apiClient.post("/invoices/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices", { protocol: id }] });
      setModalType(null);
      setNewInvoice({
        invoice_number: "",
        amount_net: "",
        is_advance_payment: false,
        mandate_type: "",
      });
      setNewInvoiceFile(null);
      setInvoiceErrors({});
      setInvoiceServerError("");
    },
    onError: (err: unknown) => {
      // Surface the real backend validation message instead of a generic one.
      const e = err as { response?: { data?: { errors?: unknown } } };
      const errors = e?.response?.data?.errors;
      let msg = "";
      if (typeof errors === "string") msg = errors;
      else if (errors && typeof errors === "object") {
        msg = Object.values(errors as Record<string, unknown>)
          .map((v) => (Array.isArray(v) ? v.join(" ") : String(v)))
          .join(" ");
      }
      setInvoiceServerError(
        msg || "Nie udało się zapisać. Sprawdź dane i spróbuj ponownie.",
      );
    },
  });

  const withdrawMutation = useMutation({
    mutationFn: () => apiClient.post(`/protocols/${id}/withdraw/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["protocols", id] });
      qc.invalidateQueries({
        queryKey: ["protocols", { order: protocol?.order }],
      });
      setModalType(null);
    },
  });

  // Usunięcie odrzuconego POCK (dyrektor techniczny / CEO) — soft delete z powodem.
  const deleteMutation = useMutation({
    mutationFn: (reason: string) => apiClient.delete(`/protocols/${id}/`, { data: { reason } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["protocols"] });
      setModalType(null);
      setDeleteReason("");
      navigate(protocol ? `/orders/${protocol.order}` : "/protocols");
    },
  });

  const forceAcceptMutation = useMutation({
    mutationFn: () => apiClient.post(`/protocols/${id}/force_accept/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["protocols", id] });
      qc.invalidateQueries({ queryKey: ["protocol-audit-logs", id] });
      qc.invalidateQueries({
        queryKey: ["protocols", { order: protocol?.order }],
      });
      setModalType(null);
    },
  });

  // Zamawiający odpowiada na końcowy „POCK bez kwot" pracownika realizującego —
  // akceptacja kończy zamówienie, odrzucenie wraca do pracownika z powodem.
  const ordererResponseMutation = useMutation({
    mutationFn: (body: { accepted: boolean; comment?: string }) =>
      apiClient.post(`/protocols/${id}/orderer-response/`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["protocols", id] });
      qc.invalidateQueries({ queryKey: ["protocol-audit-logs", id] });
      qc.invalidateQueries({
        queryKey: ["protocols", { order: protocol?.order }],
      });
      qc.invalidateQueries({ queryKey: ["orders"] });
      setOrdererComment("");
      setModalType(null);
    },
  });
  const ordererResponseError =
    (
      ordererResponseMutation.error as {
        response?: { data?: { errors?: { detail?: string } } };
      } | null
    )?.response?.data?.errors?.detail || "Nie udało się zapisać odpowiedzi. Spróbuj ponownie.";

  const uploadAttachmentMutation = useMutation({
    mutationFn: ({ itemId, files }: { itemId: string; files: File[] }) => {
      const fd = new FormData();
      files.forEach((f) => fd.append("attachment", f));
      return apiClient.patch(
        `/protocols/${id}/items/${itemId}/attachment/`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["protocols", id] });
      setUploadingItemId(null);
    },
  });

  const openModal = (inv: Invoice, type: "status" | "booking" | "payment") => {
    setSelected(inv);
    setModalType(type);
    const remainingGross = Math.max(
      0,
      parseFloat(inv.amount_gross || "0") - parseFloat(inv.paid_amount || "0"),
    );
    setForm({
      status: inv.status,
      booking_status: inv.booking_status,
      payment_amount: remainingGross > 0 ? remainingGross.toFixed(2) : "",
      payment_date: new Date().toISOString().slice(0, 10),
      comment: "",
    });
  };

  const isValidAmount = (v: string) =>
    /^\d+(\.\d+)?$/.test(v.trim()) && parseFloat(v) > 0;

  const validateAndAddInvoice = () => {
    const indiv = protocol?.contractor_entity_type === "individual";
    setInvoiceServerError("");
    const errs: typeof invoiceErrors = {};
    if (!newInvoice.invoice_number.trim())
      errs.invoice_number = indiv
        ? "Numer umowy jest wymagany"
        : "Numer faktury jest wymagany";
    if (!newInvoice.amount_net || !isValidAmount(newInvoice.amount_net)) {
      errs.amount_net = indiv
        ? "Podaj prawidłową kwotę brutto (liczbę > 0)"
        : "Podaj prawidłową kwotę netto (liczbę > 0)";
    }
    if (
      indiv &&
      newInvoice.mandate_type !== "uz" &&
      newInvoice.mandate_type !== "uod"
    ) {
      errs.mandate_type = "Wybierz typ umowy: zlecenie (UZ) lub o dzieło (UoD)";
    }
    if (newInvoiceFile && !newInvoiceFile.type.includes("pdf")) {
      errs.file = "Plik musi być w formacie PDF";
    }
    setInvoiceErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const fd = new FormData();
    fd.append("protocol", id!);
    fd.append("invoice_number", newInvoice.invoice_number);
    // Osoba fizyczna (UZ/UoD): brak VAT — księgowy podaje wyłącznie kwotę brutto,
    // która jest jednocześnie kwotą netto dokumentu (ZUS/PIT poza systemem).
    fd.append("amount_net", newInvoice.amount_net);
    fd.append("amount_gross", indiv ? newInvoice.amount_net : computedGross);
    fd.append(
      "is_advance_payment",
      newInvoice.is_advance_payment ? "true" : "false",
    );
    if (indiv) fd.append("mandate_type", newInvoice.mandate_type);
    if (newInvoiceFile) fd.append("invoice_file", newInvoiceFile);
    addInvoiceMutation.mutate(fd);
  };

  const handleSaveEdit = (protocol: Protocol) => {
    updateItemsMutation.mutate({
      is_final: editIsFinal,
      items: editItems.map((it, idx) => {
        const item = protocol.items[idx];
        const qp = parseFloat(it.quality_pct) || 100;
        return {
          id: it.id,
          completion_percentage: completionPctFromQty(
            it.qty,
            item?.order_line_item_quantity ?? "1",
            qp,
          ),
          quality_pct: qp,
          amount_net:
            it.is_manual && it.amount_net !== "" ? it.amount_net : null,
        };
      }),
    });
  };

  const handleItemAttachmentClick = (itemId: string) => {
    setUploadingItemId(itemId);
    itemFileInputRef.current?.click();
  };

  const handleItemFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length || !uploadingItemId) return;
    uploadAttachmentMutation.mutate({ itemId: uploadingItemId, files });
    e.target.value = "";
  };

  if (isLoading) return <PageLoader />;
  if (!protocol)
    return (
      <p className="text-gray-500 dark:text-gray-400">
        Nie znaleziono protokołu.
      </p>
    );

  // Natural-person (osoba fizyczna) settlements are UZ/UoD contracts issued by
  // kadry/księgowość (HR + accountant), not invoices sent by the contractor.
  const isIndividual = protocol.contractor_entity_type === "individual";
  const canIssueMandate =
    isIndividual &&
    (isCeo ||
      user?.role === "hr" ||
      user?.role === "accountant" ||
      user?.role === "director");
  // A „POCK bez kwot" (executor's hand-over) is never settled — no invoice, no UZ/UoD.
  const canAddSettlement = (isIndividual ? canIssueMandate : canManage) && !protocol.without_amounts;

  const totalCompletion =
    protocol.items.length > 0
      ? (
          protocol.items.reduce(
            (sum, it) => sum + parseFloat(it.completion_percentage),
            0,
          ) / protocol.items.length
        ).toFixed(1)
      : "—";

  const settlementPct = protocolSettlementPct(protocol);
  const totalValue = protocol.items.reduce((sum, item) => {
    const v =
      item.amount_net != null
        ? parseFloat(item.amount_net)
        : parseFloat(autoItemValue(item, settlementPct));
    return sum + (isNaN(v) ? 0 : v);
  }, 0);
  // Rate-masked items (viewer lacks finance access to this order's rates):
  // all masked → no value at all; some masked → the visible part, starred.
  const maskedCount = protocol.items.filter(isItemMasked).length;
  const totalValueLabel =
    maskedCount === protocol.items.length && protocol.items.length > 0
      ? "—"
      : `${formatAmount(totalValue)} PLN${maskedCount > 0 ? "*" : ""}`;

  const canWithdrawNow =
    canWithdraw &&
    protocol.accepted_by_contractor === null &&
    !protocol.is_withdrawn;
  const canEditNow = canEdit && !protocol.is_withdrawn;
  // Koordynator, który wystawił POCK, może go zaakceptować obustronnie tylko
  // poniżej 5000 zł netto (limit egzekwowany też po stronie backendu).
  const canCoordinatorAccept =
    user?.role === "coordinator" &&
    protocol.created_by === user.id &&
    totalValue < 5000;
  // POCK końcowy bez kwot ma własną akceptację zamawiającego (przyciski niżej) —
  // „obustronnie" nie ma tu sensu, bo podwykonawcy nie ma w obiegu.
  const canForceAccept =
    (isCeo || canCoordinatorAccept) &&
    protocol.accepted_by_contractor === null &&
    !protocol.is_withdrawn &&
    !protocol.awaiting_orderer_acceptance;

  return (
    <div className="max-w-6xl">
      {/* Back link */}
      <Link
        to={`/orders/${protocol.order}`}
        className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 mb-5"
      >
        <ArrowLeft size={14} /> Powrót do zamówienia {protocol.order_number}
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1
            className={`text-2xl font-bold ${protocol.is_withdrawn ? "line-through text-gray-400 dark:text-gray-500" : "text-gray-900 dark:text-gray-100"}`}
          >
            {protocol.protocol_number}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            <Link to={`/projects/${protocol.project}`} className="hover:underline">
              {protocol.project_name}
            </Link>{" "}
            ·{" "}
            <Link
              to={`/contractors/${protocol.contractor}`}
              className="hover:underline"
            >
              {protocol.contractor_name}
            </Link>
            {isIndividual ? (
              <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">
                (osoba fizyczna · UZ/UoD)
              </span>
            ) : protocol.contractor_is_vat_payer ? (
              <span className="ml-2 text-xs text-[var(--color-primary)]">
                (płatnik VAT)
              </span>
            ) : null}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {protocol.is_withdrawn ? (
            <Badge color="gray">Wycofany</Badge>
          ) : (
            <>
              <Badge color={protocol.is_final ? "purple" : "blue"}>
                {protocol.is_final ? "Końcowy" : "Częściowy"}
              </Badge>
              {protocol.without_amounts && <Badge color="orange" title="POCK bez kwot — wystawiony przez pracownika realizującego jako przekazanie prac; bez faktury i UZ/UoD">Bez kwot</Badge>}
              <Badge
                color={
                  protocol.accepted_by_contractor === null
                    ? "gray"
                    : protocol.accepted_by_contractor
                      ? "green"
                      : "red"
                }
              >
                {protocol.accepted_by_contractor === null
                  ? protocol.awaiting_orderer_acceptance
                    ? "Oczekuje na akceptację zamawiającego"
                    : "Oczekuje na akceptację"
                  : protocol.accepted_by_contractor
                    ? "Zaakceptowany"
                    : "Odrzucony"}
              </Badge>
            </>
          )}
        </div>
      </div>

      {/* Action bar */}
      {!editMode && (
        <div className="flex items-center gap-2 mb-6">
          {canEditNow && (
            <Button size="sm" variant="secondary" onClick={enterEditMode}>
              <Edit2 size={14} /> Edytuj
            </Button>
          )}
          {canWithdrawNow && (
            <Button
              size="sm"
              variant="danger"
              onClick={() => setModalType("withdraw")}
            >
              <RotateCcw size={14} /> Wycofaj POCK
            </Button>
          )}
          {canForceAccept && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setModalType("force_accept")}
            >
              Oznacz jako zaakceptowany obustronnie
            </Button>
          )}
          {protocol.can_respond_as_orderer && (
            <>
              <Button
                size="sm"
                variant="primary"
                onClick={() => setModalType("orderer_accept")}
              >
                Zaakceptuj jako zamawiający
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={() => setModalType("orderer_reject")}
              >
                Odrzuć
              </Button>
            </>
          )}
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setModalType("note")}
          >
            <Plus size={14} /> Notatka
          </Button>
          {protocol.can_delete && (
            <Button
              size="sm"
              variant="danger"
              onClick={() => {
                deleteMutation.reset();
                setModalType("delete");
              }}
            >
              <Trash2 size={14} /> Usuń POCK
            </Button>
          )}
        </div>
      )}

      {/* Meta */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <InfoCard label="Wystawił" value={protocol.created_by_name} />
        <InfoCard
          label="Data wystawienia"
          value={new Date(protocol.created_at).toLocaleDateString("pl")}
        />
        <InfoCard label="Śr. realizacja" value={`${totalCompletion}%`} />
        <InfoCard
          label="Wartość POCK (netto)"
          value={totalValueLabel}
        />
      </div>

      {(protocol.source_protocol_number || (protocol.corrections ?? []).length > 0) && (
        <div className="bg-gray-50 dark:bg-gray-800/50 p-4 mb-4 flex flex-col gap-1 text-sm" data-testid="protocol-corrections">
          <p className="font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
            <GitBranch size={14} className="text-gray-400 dark:text-gray-500" /> Historia korekt
          </p>
          {protocol.source_protocol_number && (
            <p className="text-gray-600 dark:text-gray-400">
              Utworzony na podstawie POCK{" "}
              {protocol.source_protocol && !protocol.source_protocol_deleted ? (
                <Link to={`/protocols/${protocol.source_protocol}`} className="text-[var(--color-primary)] hover:underline">
                  {protocol.source_protocol_number}
                </Link>
              ) : (
                <span className="line-through">{protocol.source_protocol_number}</span>
              )}
              {protocol.source_protocol_deleted && " (usunięty)"}
            </p>
          )}
          {(protocol.corrections ?? []).map((c) => (
            <p key={c.id} className="text-gray-600 dark:text-gray-400">
              Korekta:{" "}
              <Link to={`/protocols/${c.id}`} className="text-[var(--color-primary)] hover:underline">
                {c.protocol_number}
              </Link>{" "}
              · {new Date(c.created_at).toLocaleDateString("pl")} · {CORRECTION_STATUS_LABEL[c.status_contractor_response]}
            </p>
          ))}
        </div>
      )}

      <DocumentAttachmentsPanel
        attachments={protocol.document_attachments ?? []}
        endpoint={`/protocols/${protocol.id}/attachments/`}
        canUpload={protocol.can_add_attachments === true}
        invalidateKeys={[["protocols", id], ["protocol-audit-logs", id]]}
        generatedPdf={protocol.generated_pdf_managed_file_detail ?? null}
        hint={
          protocol.can_add_attachments
            ? "Pliki całego protokołu (skan podpisanego protokołu, korespondencja, dokumentacja odbiorowa) — także po akceptacji. Pliki konkretnej pozycji dodajesz w tabeli pozycji."
            : undefined
        }
      />

      {protocol.notes && (
        <div className="bg-gray-50 dark:bg-gray-800/50 p-4 mb-4">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Uwagi
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {protocol.notes}
          </p>
        </div>
      )}

      {protocol.contractor_rejection_comment && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm font-medium text-red-700 mb-1">
            Komentarz odrzucenia
          </p>
          <p className="text-sm text-red-600">
            {protocol.contractor_rejection_comment}
          </p>
        </div>
      )}

      {/* Items — view or edit */}
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
        Pozycje protokołu
      </h2>

      {editMode ? (
        /* --- EDIT MODE --- */
        <div className="mb-6">
          <label className="flex items-center gap-2 mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={editIsFinal}
              onChange={(e) => setEditIsFinal(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Odbiór końcowy
            </span>
          </label>

          <div className="bg-white dark:bg-gray-800 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                    Pozycja
                  </th>
                  <th className="text-right px-4 py-2 w-20 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                    Zam.
                  </th>
                  <th className="text-right px-4 py-2 w-28 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                    Ilość odbioru
                  </th>
                  <th className="text-right px-4 py-2 w-24 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                    Jakość %
                  </th>
                  <th className="text-right px-4 py-2 w-44 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                    Kwota netto (PLN)
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {protocol.items.map((item) => {
                  const editItem = editItems.find((e) => e.id === item.id);
                  if (!editItem) return null;
                  const q = parseFloat(editItem.qty) || 0;
                  const qp = parseFloat(editItem.quality_pct) || 100;
                  const rate = parseFloat(item.order_line_item_unit_rate ?? "") || 0;
                  // Settlement % is part of the stored amount (approval applies
                  // it), so the edit form has to show it too — otherwise a POCK
                  // settled below 100% displays a value nobody will ever book.
                  const autoAmount = amountFromQty(q, rate, qp, settlementPct).toFixed(2);
                  const displayAmount =
                    editItem.is_manual && editItem.amount_net !== ""
                      ? editItem.amount_net
                      : autoAmount;
                  const isOverridden =
                    editItem.is_manual &&
                    editItem.amount_net !== "" &&
                    editItem.amount_net !== autoAmount;

                  return (
                    <tr key={item.id}>
                      <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                        {item.order_line_item_name}
                        <span className="text-gray-400 dark:text-gray-500 ml-1 text-xs">
                          {displayUnit(item.order_line_item_unit)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">
                        {item.order_line_item_quantity}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <input
                          type="number"
                          min="0"
                          max={item.order_line_item_quantity}
                          step="0.01"
                          value={editItem.qty}
                          onChange={(e) => {
                            const newQty = e.target.value;
                            updateEditItem(item.id, "qty", newQty);
                            if (!editItem.is_manual) {
                              const nq = parseFloat(newQty) || 0;
                              updateEditItem(
                                item.id,
                                "amount_net",
                                amountFromQty(nq, rate, qp, settlementPct).toFixed(2),
                              );
                            }
                          }}
                          className="w-24 text-right rounded border border-gray-300 px-2 py-1"
                        />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={editItem.quality_pct}
                          onChange={(e) => {
                            const newQp = e.target.value;
                            updateEditItem(item.id, "quality_pct", newQp);
                            if (!editItem.is_manual) {
                              const nqp = parseFloat(newQp) || 100;
                              updateEditItem(
                                item.id,
                                "amount_net",
                                amountFromQty(q, rate, nqp, settlementPct).toFixed(2),
                              );
                            }
                          }}
                          className="w-20 text-right rounded border border-gray-300 px-2 py-1"
                        />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex flex-col items-end gap-0.5">
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={displayAmount}
                              onChange={(e) => {
                                updateEditItem(
                                  item.id,
                                  "amount_net",
                                  e.target.value,
                                );
                                updateEditItem(item.id, "is_manual", "true");
                              }}
                              onBlur={(e) => {
                                if (
                                  e.target.value === "" ||
                                  e.target.value === autoAmount
                                ) {
                                  updateEditItem(item.id, "is_manual", "false");
                                  updateEditItem(item.id, "amount_net", "");
                                }
                              }}
                              className={`w-28 text-right rounded border px-2 py-1 text-sm ${
                                isOverridden
                                  ? "border-red-400 bg-red-50 text-red-700 font-semibold"
                                  : "border-gray-300"
                              }`}
                            />
                            {editItem.is_manual && (
                              <button
                                type="button"
                                title="Przywróć wartość automatyczną"
                                onClick={() => {
                                  updateEditItem(item.id, "is_manual", "false");
                                  updateEditItem(item.id, "amount_net", "");
                                }}
                                className="text-xs text-gray-400 dark:text-gray-500 hover:text-red-500"
                              >
                                ↺
                              </button>
                            )}
                          </div>
                          {isOverridden && (
                            <span className="text-xs text-red-400">
                              Domyślnie wyliczona: {formatAmount(autoAmount)}{" "}
                              PLN
                            </span>
                          )}
                          {!editItem.is_manual && (
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              (auto)
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {updateItemsMutation.isError && (
            <p className="text-sm text-red-600 mt-2">
              Błąd zapisu. Spróbuj ponownie.
            </p>
          )}
          <div className="flex gap-3 mt-4">
            <Button
              loading={updateItemsMutation.isPending}
              onClick={() => handleSaveEdit(protocol)}
            >
              Zapisz zmiany
            </Button>
            <Button variant="secondary" onClick={() => setEditMode(false)}>
              Anuluj
            </Button>
          </div>
        </div>
      ) : (
        /* --- VIEW MODE --- */
        <div className="bg-white dark:bg-gray-800 overflow-hidden mb-8">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Pozycja
                </th>
                <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Zam. ilość
                </th>
                <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Realizacja
                </th>
                <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  % rozl.
                </th>
                <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Kwota netto
                </th>
                <th className="text-center px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Załącznik
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {protocol.items.map((item) => {
                const pct = parseFloat(item.completion_percentage);
                const auto = autoItemValue(item, settlementPct);
                const displayAmount = isItemMasked(item)
                  ? "—"
                  : item.amount_net != null
                    ? `${formatAmount(item.amount_net)} PLN`
                    : `${formatAmount(auto)} PLN`;
                const isOverridden = isAmountOverridden(item.amount_net, auto);

                return (
                  <tr key={item.id}>
                    <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                      {item.order_line_item_name}
                      <span className="text-gray-400 dark:text-gray-500 ml-1 text-xs">
                          {displayUnit(item.order_line_item_unit)}
                        </span>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">
                      {item.order_line_item_quantity}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span
                        className={
                          pct >= 100
                            ? "text-green-700 font-semibold"
                            : pct > 0
                              ? "text-yellow-700 font-semibold"
                              : "text-gray-400 dark:text-gray-500"
                        }
                      >
                        {pct.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">
                      {Math.round(settlementPct)}%
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span
                        className={
                          isOverridden
                            ? "text-red-600 font-semibold"
                            : "text-gray-800 dark:text-gray-200"
                        }
                      >
                        {displayAmount}
                      </span>
                      {isOverridden && (
                        <div className="text-xs text-red-400">
                          (Domyślnie wyliczona: {formatAmount(auto)} PLN)
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const current = item.attachment_versions.filter(
                          (v) => !v.is_superseded,
                        );
                        const history = item.attachment_versions.filter(
                          (v) => v.is_superseded,
                        );
                        let currentIdx = 0;
                        return (
                          <div className="flex flex-col gap-0.5 items-start">
                            {current.length === 0 && !canEdit && (
                              <span className="text-xs text-gray-400 dark:text-gray-500">
                                —
                              </span>
                            )}
                            {current.map((v) => {
                              currentIdx += 1;
                              return (
                                <a
                                  key={v.id}
                                  href={v.managed_file_detail?.url ?? "#"}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-[var(--color-primary)] text-xs hover:underline"
                                >
                                  <Paperclip size={12} />
                                  {v.original_name || `Aktualny ${currentIdx}`}
                                </a>
                              );
                            })}
                            {canEdit && !protocol.is_withdrawn && (
                              <button
                                onClick={() =>
                                  handleItemAttachmentClick(item.id)
                                }
                                disabled={
                                  uploadAttachmentMutation.isPending &&
                                  uploadingItemId === item.id
                                }
                                className="inline-flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)] transition-colors rounded px-1.5 py-0.5 mt-1"
                              >
                                <Upload size={10} />
                                {uploadAttachmentMutation.isPending &&
                                uploadingItemId === item.id
                                  ? "Wgrywanie..."
                                  : current.length > 0
                                    ? "Zamień"
                                    : "Dodaj"}
                              </button>
                            )}
                            {history.length > 0 && (
                              <details className="mt-1">
                                <summary className="text-xs text-gray-400 dark:text-gray-500 cursor-pointer hover:text-gray-600 select-none">
                                  Historia ({history.length})
                                </summary>
                                <div className="flex flex-col gap-0.5 mt-0.5 pl-2 border-l border-gray-200 dark:border-gray-700">
                                  {history.map((v) => (
                                    <a
                                      key={v.id}
                                      href={v.managed_file_detail?.url ?? "#"}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 hover:underline"
                                    >
                                      <Paperclip size={10} />
                                      {v.original_name || "Plik"} ·{" "}
                                      {new Date(
                                        v.created_at,
                                      ).toLocaleDateString("pl")}
                                    </a>
                                  ))}
                                </div>
                              </details>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-gray-50 dark:bg-gray-800/50">
                <td
                  colSpan={4}
                  className="px-4 py-2 text-right text-sm font-semibold text-gray-700 dark:text-gray-300"
                >
                  Łącznie netto:
                </td>
                <td className="px-4 py-2 text-right text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {totalValueLabel}
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <input
        ref={itemFileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleItemFileSelected}
      />

      {/* Audit log history */}
      <div className="mb-8">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 mb-3">
          <Clock size={16} className="text-gray-400 dark:text-gray-500" />{" "}
          Historia zmian
        </h2>
        {(auditLogs ?? []).length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">
            Brak zapisów historii.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {(auditLogs ?? []).map((log) => (
              <div
                key={log.id}
                className="bg-gray-50 dark:bg-gray-800/50 px-4 py-3"
              >
                <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                  {log.description}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  {log.user_name} ·{" "}
                  {new Date(log.created_at).toLocaleString("pl")}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invoices */}
      {!protocol.is_withdrawn && (
        <>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {isIndividual ? "Rozliczenie — umowa (UZ/UoD)" : "Faktury P-GK"}
            </h2>
            {canAddSettlement && (
              <Button
                size="sm"
                onClick={() => {
                  setInvoiceErrors({});
                  setInvoiceServerError("");
                  setNewInvoice((f) => ({
                    ...f,
                    amount_net: totalValue > 0 ? totalValue.toFixed(2) : "",
                  }));
                  setModalType("add");
                }}
              >
                {isIndividual ? "+ Wystaw UZ/UoD" : "+ Dodaj fakturę"}
              </Button>
            )}
          </div>
          {isIndividual && (
            <p className="text-xs text-gray-500 dark:text-gray-400 -mt-1 mb-3">
              Podwykonawca jest osobą fizyczną — rozliczenie następuje umową
              zlecenia/o dzieło wystawianą przez kadry/księgowość, nie fakturą
              od podwykonawcy.
            </p>
          )}

          {invoicesLoading ? (
            <Spinner size="sm" />
          ) : (invoices ?? []).length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Brak faktur powiązanych z tym protokołem.
            </p>
          ) : (
            <div className="bg-white dark:bg-gray-800 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                      Nr faktury
                    </th>
                    <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      Netto
                    </th>
                    <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      Brutto
                    </th>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                      Status
                    </th>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                      Księg.
                    </th>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                      Płatność
                    </th>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                      Plik
                    </th>
                    {canManage && (
                      <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        Akcje
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(invoices ?? []).map((inv) => {
                    const isWithdrawn = inv.status === "withdrawn";
                    const versionCount = (inv.file_versions ?? []).length;
                    const showRejection =
                      inv.status === "rejected" && inv.status_comment;
                    const totalCols = 7 + (canManage ? 1 : 0);
                    return (
                      <Fragment key={inv.id}>
                        <tr
                          className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${isWithdrawn ? "opacity-60" : ""}`}
                        >
                          <td className="px-3 py-3 font-medium text-gray-900 dark:text-gray-100">
                            <div className="flex flex-col gap-0.5">
                              <Link
                                to={`/invoices/${inv.id}`}
                                className={`hover:text-[var(--color-primary)] hover:underline inline-flex items-center gap-1 whitespace-nowrap ${isWithdrawn ? "line-through text-gray-400 dark:text-gray-500" : ""}`}
                              >
                                {inv.invoice_number}{" "}
                                <ExternalLink
                                  size={11}
                                  className="text-gray-400 dark:text-gray-500"
                                />
                              </Link>
                              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                                <InvoiceAiBadge analysis={inv.ai_analysis} />
                                {!inv.mandate_type && (
                                  <button
                                    type="button"
                                    onClick={() => analyzeMutation.mutate(inv.id)}
                                    disabled={
                                      analyzeMutation.isPending &&
                                      analyzeMutation.variables === inv.id
                                    }
                                    title={inv.ai_analysis ? "Odśwież analizę AI" : "Uruchom analizę AI"}
                                    className="text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 disabled:opacity-50"
                                  >
                                    <RotateCcw
                                      size={13}
                                      className={
                                        analyzeMutation.isPending &&
                                        analyzeMutation.variables === inv.id
                                          ? "animate-spin"
                                          : ""
                                      }
                                    />
                                  </button>
                                )}
                              </span>
                              {inv.is_advance_payment && (
                                <Badge color="yellow">Zaliczkowa</Badge>
                              )}
                              {inv.mandate_type && (
                                <Badge color="blue">
                                  {inv.mandate_type_label}
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-3 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            {inv.amount_net != null ? `${formatAmount(inv.amount_net)} PLN` : "—"}
                          </td>
                          <td className="px-3 py-3 text-right font-medium whitespace-nowrap">
                            {inv.amount_gross != null ? `${formatAmount(inv.amount_gross)} PLN` : "—"}
                          </td>
                          <td className="px-3 py-3">
                            <Badge color={statusColor[inv.status] ?? "gray"}>
                              {statusLabel[inv.status] ?? inv.status}
                            </Badge>
                          </td>
                          <td className="px-3 py-3">
                            <Badge
                              color={
                                inv.booking_status === "booked"
                                  ? "green"
                                  : "gray"
                              }
                            >
                              {inv.booking_status === "booked"
                                ? "Zaksięgowana"
                                : "Niezaksięgowana"}
                            </Badge>
                          </td>
                          <td className="px-3 py-3">
                            <Badge
                              color={paymentColor[inv.payment_status] ?? "gray"}
                            >
                              {paymentLabel[inv.payment_status] ??
                                inv.payment_status}
                            </Badge>
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex flex-col gap-0.5 items-start">
                              <ManagedFileLink
                                file={inv.invoice_managed_file_detail}
                                icon={<Paperclip size={12} />}
                                className="whitespace-nowrap"
                              />
                              {versionCount > 0 && (
                                <div className="flex flex-wrap gap-x-1 gap-y-0 mt-0.5">
                                  {(inv.file_versions ?? []).map((v, vi) => (
                                    <a
                                      key={v.id}
                                      href={v.managed_file_detail?.url ?? "#"}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title={`v${versionCount - vi} · ${new Date(v.created_at).toLocaleString("pl")} (${v.uploaded_by_name})`}
                                      className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 hover:underline whitespace-nowrap"
                                    >
                                      v{versionCount - vi}
                                    </a>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                          {canManage && (
                            <td className="px-3 py-3 text-right">
                              {!isWithdrawn && (
                                <div className="flex items-center justify-end gap-1 text-xs whitespace-nowrap">
                                  <button
                                    onClick={() => openModal(inv, "status")}
                                    className="text-[var(--color-primary)] hover:underline"
                                  >
                                    Status
                                  </button>
                                  <span className="text-gray-300">|</span>
                                  <button
                                    onClick={() => openModal(inv, "booking")}
                                    className="text-[var(--color-primary)] hover:underline"
                                  >
                                    Ks.
                                  </button>
                                  <span className="text-gray-300">|</span>
                                  <button
                                    onClick={() => openModal(inv, "payment")}
                                    className="text-[var(--color-primary)] hover:underline"
                                  >
                                    Płatność
                                  </button>
                                </div>
                              )}
                            </td>
                          )}
                        </tr>
                        {showRejection && (
                          <tr className="bg-red-50/50 dark:bg-red-900/10">
                            <td colSpan={totalCols} className="px-3 py-2">
                              <div className="flex gap-2 items-start">
                                <p className="text-[10px] font-mono uppercase tracking-widest text-red-500 dark:text-red-400 whitespace-nowrap pt-0.5">
                                  Powód odrzucenia
                                </p>
                                <p className="text-xs text-red-700 dark:text-red-300 whitespace-pre-wrap break-words">
                                  {inv.status_comment}
                                </p>
                              </div>
                            </td>
                          </tr>
                        )}
                        {!isWithdrawn &&
                          (inv.ai_analysis?.status === "error" ||
                            inv.ai_analysis?.status === "warning") && (
                            <tr>
                              <td colSpan={totalCols} className="px-3 pb-3">
                                <InvoiceAiInlineAlert analysis={inv.ai_analysis} />
                              </td>
                            </tr>
                          )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Note modal */}
      <Modal
        open={modalType === "note"}
        onClose={() => setModalType(null)}
        title="Dodaj notatkę"
        size="sm"
      >
        <div className="flex flex-col gap-4">
          <textarea
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            rows={4}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Opisz zmianę lub dodaj komentarz..."
            autoFocus
          />
          <div className="flex gap-3">
            <Button
              loading={addNoteMutation.isPending}
              onClick={() =>
                noteText.trim() && addNoteMutation.mutate(noteText.trim())
              }
              disabled={!noteText.trim()}
            >
              Zapisz
            </Button>
            <Button variant="secondary" onClick={() => setModalType(null)}>
              Anuluj
            </Button>
          </div>
        </div>
      </Modal>

      {/* Usunięcie odrzuconego POCK (dyrektor techniczny / CEO) */}
      <Modal
        open={modalType === "delete"}
        onClose={() => setModalType(null)}
        title="Usuń odrzucony POCK"
        size="sm"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Protokół <strong>{protocol.protocol_number}</strong> zniknie z listy protokołów i z
            kartoteki zamówienia. W historii zamówienia zostanie wpis: kto, kiedy i z jakiego powodu go usunął.
          </p>
          <label className="flex flex-col gap-1 text-sm text-gray-700 dark:text-gray-300">
            Powód usunięcia (wymagany)
            <textarea
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              rows={3}
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              placeholder="np. Zastąpiony poprawionym protokołem"
            />
          </label>
          {deleteMutation.isError && (
            <p className="text-sm text-red-600 dark:text-red-400">
              {apiErrorText(deleteMutation.error, "Nie udało się usunąć protokołu.")}
            </p>
          )}
          <div className="flex gap-3">
            <Button
              variant="danger"
              loading={deleteMutation.isPending}
              disabled={!deleteReason.trim()}
              onClick={() => deleteMutation.mutate(deleteReason.trim())}
            >
              Usuń POCK
            </Button>
            <Button variant="secondary" onClick={() => setModalType(null)}>
              Anuluj
            </Button>
          </div>
        </div>
      </Modal>

      {/* Withdraw confirmation modal */}
      <Modal
        open={modalType === "withdraw"}
        onClose={() => setModalType(null)}
        title="Wycofaj protokół POCK"
        size="sm"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Czy na pewno chcesz wycofać protokół{" "}
            <strong>{protocol.protocol_number}</strong>? Operacja jest
            nieodwracalna.
          </p>
          {withdrawMutation.isError && (
            <p className="text-sm text-red-600">
              Błąd wycofania. Spróbuj ponownie.
            </p>
          )}
          <div className="flex gap-3">
            <Button
              variant="danger"
              loading={withdrawMutation.isPending}
              onClick={() => withdrawMutation.mutate()}
            >
              Wycofaj POCK
            </Button>
            <Button variant="secondary" onClick={() => setModalType(null)}>
              Anuluj
            </Button>
          </div>
        </div>
      </Modal>

      {/* Zamawiający: akceptacja / odrzucenie końcowego POCK bez kwot */}
      <Modal
        open={modalType === "orderer_accept"}
        onClose={() => setModalType(null)}
        title="Zaakceptuj POCK jako zamawiający"
        size="sm"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Protokół <strong>{protocol.protocol_number}</strong> to końcowe
            przekazanie prac przez pracownika GK ({protocol.created_by_name}).
            Po akceptacji zamówienie zostanie oznaczone jako zakończone.
          </p>
          {ordererResponseMutation.isError && (
            <p className="text-sm text-red-600">{ordererResponseError}</p>
          )}
          <div className="flex gap-3">
            <Button
              variant="primary"
              loading={ordererResponseMutation.isPending}
              onClick={() => ordererResponseMutation.mutate({ accepted: true })}
            >
              Zaakceptuj
            </Button>
            <Button variant="secondary" onClick={() => setModalType(null)}>
              Anuluj
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        open={modalType === "orderer_reject"}
        onClose={() => setModalType(null)}
        title="Odrzuć POCK"
        size="sm"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Podaj powód odrzucenia protokołu{" "}
            <strong>{protocol.protocol_number}</strong> — trafi do pracownika,
            który przekazał prace. Zamówienie pozostanie w realizacji.
          </p>
          <textarea
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            rows={3}
            placeholder="Powód odrzucenia"
            aria-label="Powód odrzucenia"
            value={ordererComment}
            onChange={(e) => setOrdererComment(e.target.value)}
          />
          {ordererResponseMutation.isError && (
            <p className="text-sm text-red-600">{ordererResponseError}</p>
          )}
          <div className="flex gap-3">
            <Button
              variant="danger"
              disabled={!ordererComment.trim()}
              loading={ordererResponseMutation.isPending}
              onClick={() =>
                ordererResponseMutation.mutate({
                  accepted: false,
                  comment: ordererComment.trim(),
                })
              }
            >
              Odrzuć POCK
            </Button>
            <Button variant="secondary" onClick={() => setModalType(null)}>
              Anuluj
            </Button>
          </div>
        </div>
      </Modal>

      {/* Force accept confirmation modal */}
      <Modal
        open={modalType === "force_accept"}
        onClose={() => setModalType(null)}
        title="Oznacz jako zaakceptowany obustronnie"
        size="sm"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Czy na pewno chcesz oznaczyć protokół{" "}
            <strong>{protocol.protocol_number}</strong> jako zaakceptowany
            obustronnie? Podwykonawca nie będzie mógł już odpowiedzieć na ten
            protokół.
          </p>
          {forceAcceptMutation.isError && (
            <p className="text-sm text-red-600">
              Błąd operacji. Spróbuj ponownie.
            </p>
          )}
          <div className="flex gap-3">
            <Button
              variant="primary"
              loading={forceAcceptMutation.isPending}
              onClick={() => forceAcceptMutation.mutate()}
            >
              Zatwierdź obustronnie
            </Button>
            <Button variant="secondary" onClick={() => setModalType(null)}>
              Anuluj
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add invoice modal */}
      <Modal
        open={modalType === "add"}
        onClose={() => setModalType(null)}
        title={isIndividual ? "Wystaw UZ/UoD" : "Dodaj fakturę P-GK"}
        size="sm"
      >
        <div className="flex flex-col gap-4">
          {isIndividual && (
            <div>
              <Select
                label="Typ umowy"
                value={newInvoice.mandate_type}
                onChange={(e) => {
                  setNewInvoice({
                    ...newInvoice,
                    mandate_type: e.target.value,
                  });
                  setInvoiceErrors((prev) => ({
                    ...prev,
                    mandate_type: undefined,
                  }));
                }}
                options={[
                  { value: "", label: "— wybierz —" },
                  { value: "uz", label: "Umowa zlecenie (UZ)" },
                  { value: "uod", label: "Umowa o dzieło (UoD)" },
                ]}
              />
              {invoiceErrors.mandate_type && (
                <p className="text-xs text-red-600 mt-0.5">
                  {invoiceErrors.mandate_type}
                </p>
              )}
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              {isIndividual ? "Nr umowy" : "Nr faktury"}
            </label>
            <input
              type="text"
              value={newInvoice.invoice_number}
              onChange={(e) =>
                setNewInvoice({ ...newInvoice, invoice_number: e.target.value })
              }
              className={`block w-full rounded-md border px-3 py-2 text-sm ${invoiceErrors.invoice_number ? "border-red-400" : "border-gray-300"}`}
              placeholder={
                isIndividual ? "np. UZ/2026/001" : "np. FV/FSiec/2026/001"
              }
            />
            {invoiceErrors.invoice_number && (
              <p className="text-xs text-red-600 mt-0.5">
                {invoiceErrors.invoice_number}
              </p>
            )}
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              {isIndividual ? "Kwota brutto (PLN)" : "Kwota netto (PLN)"}
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={newInvoice.amount_net}
              onChange={(e) => {
                const v = e.target.value;
                setNewInvoice({ ...newInvoice, amount_net: v });
                if (v && !isValidAmount(v)) {
                  setInvoiceErrors((prev) => ({
                    ...prev,
                    amount_net: "Podaj prawidłową liczbę > 0",
                  }));
                } else {
                  setInvoiceErrors((prev) => ({
                    ...prev,
                    amount_net: undefined,
                  }));
                }
              }}
              className={`block w-full rounded-md border px-3 py-2 text-sm ${invoiceErrors.amount_net ? "border-red-400" : "border-gray-300"}`}
              placeholder="np. 1000.00"
            />
            {invoiceErrors.amount_net && (
              <p className="text-xs text-red-600 mt-0.5">
                {invoiceErrors.amount_net}
              </p>
            )}
            {isIndividual && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Kwota brutto umowy. Rozliczenie ZUS/PIT prowadzą kadry poza
                systemem.
              </p>
            )}
            {isIndividual &&
              isValidAmount(newInvoice.amount_net) &&
              Math.abs(parseFloat(newInvoice.amount_net) - totalValue) > 0.005 && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  Uwaga: kwota różni się od wartości POCK (
                  {formatAmount(totalValue.toFixed(2))} PLN). Dla UZ/UoD jest to
                  dozwolone — upewnij się, że to celowe.
                </p>
              )}
          </div>
          {!isIndividual && (
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-md px-3 py-2">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
                Kwota brutto{" "}
                {protocol?.contractor_is_vat_payer
                  ? "(+VAT 23% — podwykonawca jest podatnikiem VAT)"
                  : "(bez VAT — podwykonawca nie jest podatnikiem VAT)"}
              </p>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {formatAmount(computedGross)} PLN
              </p>
            </div>
          )}
          {!isIndividual && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={newInvoice.is_advance_payment}
                onChange={(e) =>
                  setNewInvoice({
                    ...newInvoice,
                    is_advance_payment: e.target.checked,
                  })
                }
                className="rounded"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Faktura zaliczkowa
              </span>
            </label>
          )}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              {isIndividual
                ? "Podpisana umowa (tylko PDF)"
                : "Plik faktury (tylko PDF)"}
            </label>
            <FileDropZone
              files={newInvoiceFile ? [newInvoiceFile] : []}
              onFilesChange={(fs) => {
                const f = fs[0] ?? null;
                if (f && !f.type.includes("pdf")) {
                  setInvoiceErrors((prev) => ({
                    ...prev,
                    file: "Plik musi być w formacie PDF",
                  }));
                  return;
                }
                setNewInvoiceFile(f);
                setInvoiceErrors((prev) => ({ ...prev, file: undefined }));
              }}
              accept="application/pdf"
            />
            {invoiceErrors.file && (
              <p className="text-xs text-red-600 mt-0.5">
                {invoiceErrors.file}
              </p>
            )}
          </div>
          {invoiceServerError && (
            <p className="text-sm text-red-600 break-words">
              {invoiceServerError}
            </p>
          )}
          <Button
            loading={addInvoiceMutation.isPending}
            onClick={validateAndAddInvoice}
          >
            {isIndividual ? "Zapisz umowę" : "Zapisz fakturę"}
          </Button>
        </div>
      </Modal>

      {/* Status / Booking / Payment modals */}
      <Modal
        open={modalType === "status"}
        onClose={() => setModalType(null)}
        title="Zmień status faktury"
        size="sm"
      >
        <div className="flex flex-col gap-4">
          <Select
            label="Status"
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            options={[
              { value: "submitted", label: "Przesłana" },
              { value: "accepted", label: "Przyjęta" },
              { value: "rejected", label: "Nieprzyjęta" },
            ]}
          />
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Komentarz
            </label>
            <textarea
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              rows={2}
              value={form.comment}
              onChange={(e) => setForm({ ...form, comment: e.target.value })}
            />
          </div>
          <Button
            loading={actionMutation.isPending}
            onClick={() =>
              actionMutation.mutate({
                action: "update_status",
                payload: { status: form.status, status_comment: form.comment },
              })
            }
          >
            Zapisz
          </Button>
        </div>
      </Modal>

      <Modal
        open={modalType === "booking"}
        onClose={() => setModalType(null)}
        title="Zmień status zaksięgowania"
        size="sm"
      >
        <div className="flex flex-col gap-4">
          <Select
            label="Status zaksięgowania"
            value={form.booking_status}
            onChange={(e) =>
              setForm({ ...form, booking_status: e.target.value })
            }
            options={[
              { value: "not_booked", label: "Niezaksięgowana" },
              { value: "booked", label: "Zaksięgowana" },
            ]}
          />
          <Button
            loading={actionMutation.isPending}
            onClick={() =>
              actionMutation.mutate({
                action: "update_booking",
                payload: { booking_status: form.booking_status },
              })
            }
          >
            Zapisz
          </Button>
        </div>
      </Modal>

      <Modal
        open={modalType === "payment"}
        onClose={() => setModalType(null)}
        title="Zarejestruj płatność"
        size="sm"
      >
        <div className="flex flex-col gap-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Status płatności jest wyliczany automatycznie z zarejestrowanych
            płatności.
          </p>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Kwota zapłacona brutto (PLN)
              <span className="text-red-500 ml-0.5">*</span>
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.payment_amount}
              onChange={(e) =>
                setForm({ ...form, payment_amount: e.target.value })
              }
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Data płatności<span className="text-red-500 ml-0.5">*</span>
            </label>
            <DatePicker
              value={form.payment_date}
              onChange={(v) => setForm({ ...form, payment_date: v })}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Komentarz
            </label>
            <textarea
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              rows={2}
              value={form.comment}
              onChange={(e) => setForm({ ...form, comment: e.target.value })}
            />
          </div>
          <Button
            loading={addPaymentMutation.isPending}
            disabled={!isValidAmount(form.payment_amount) || !form.payment_date}
            onClick={() =>
              addPaymentMutation.mutate({
                amount_gross: form.payment_amount,
                payment_date: form.payment_date,
                comment: form.comment,
              })
            }
          >
            Zapisz płatność
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white dark:bg-gray-800 p-4">
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</p>
      <p className="font-medium text-gray-900 dark:text-gray-100">{value}</p>
    </div>
  );
}
